import configFile from '../config.js';

// Derive backend origin from serviceUrl for validating postMessage
// serviceUrl is like "http://localhost:9380/api/v1", we need "http://localhost:9380"
const getValidOrigin = () => {
  try {
    const url = new URL(configFile.serviceUrl);
    return url.origin;
  } catch {
    return 'http://localhost:9380'; // fallback
  }
};

/**
 * Opens a centered popup window
 * @param {string} url - URL to open
 * @param {string} name - Window name
 * @param {number} width - Popup width
 * @param {number} height - Popup height
 * @returns {Window|null} Popup window reference or null if blocked
 */
const openCenteredPopup = (url, name = 'oauth_popup', width = 600, height = 700) => {
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'menubar=no',
    'toolbar=no',
    'location=yes',
    'status=no',
    'resizable=yes',
    'scrollbars=yes',
  ].join(',');

  return window.open(url, name, features);
};

/**
 * Opens OAuth authorization URL in a popup and waits for the result
 * @param {string} authUrl - The authorization URL to open
 * @returns {Promise<Object>} Resolves with success data or rejects with error
 */
export const openOAuthPopup = (authUrl) => {
  return new Promise((resolve, reject) => {
    const VALID_ORIGIN = getValidOrigin();
    
    // Track if we've already handled a response
    let hasReceivedResponse = false;
    let isCleanedUp = false;
    
    // Open the popup
    const popup = openCenteredPopup(authUrl);

    // Check if popup was blocked
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      reject(new Error('POPUP_BLOCKED'));
      return;
    }

    // Polling interval to check if popup was closed manually
    let popupCheckInterval = null;
    let timeoutId = null;

    // Cleanup function to remove listeners and intervals
    const cleanup = () => {
      if (isCleanedUp) return;
      isCleanedUp = true;
      
      window.removeEventListener('message', handleMessage);
      if (popupCheckInterval) {
        clearInterval(popupCheckInterval);
        popupCheckInterval = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    // Message handler for postMessage from popup
    const handleMessage = (event) => {
      // Skip if we already handled a response
      if (hasReceivedResponse) return;
      
      // Security: Validate origin - accept from backend origin
      // The callback page is served by the backend, so origin will be backend's
      if (event.origin !== VALID_ORIGIN) {
        // Log but don't return - some browsers report different origins
        console.warn('Received message from origin:', event.origin, 'expected:', VALID_ORIGIN);
        // Still try to process if it looks like our OAuth message
        if (!event.data?.type?.startsWith('oauth_')) {
          return;
        }
      }

      const { type, provider, agent_id, message, error_code } = event.data || {};

      // Handle success message
      if (type === 'oauth_success') {
        hasReceivedResponse = true;
        cleanup();
        // Close popup after a small delay to ensure message is processed
        setTimeout(() => {
          if (popup && !popup.closed) {
            popup.close();
          }
        }, 100);
        resolve({
          success: true,
          provider,
          agentId: agent_id,
          message: message || 'Successfully connected!',
        });
        return;
      }

      // Handle error message
      if (type === 'oauth_error') {
        hasReceivedResponse = true;
        cleanup();
        setTimeout(() => {
          if (popup && !popup.closed) {
            popup.close();
          }
        }, 100);
        reject({
          success: false,
          errorCode: error_code,
          message: message || 'OAuth authorization failed',
        });
        return;
      }
    };

    // Add message listener
    window.addEventListener('message', handleMessage);

    // Poll to check if popup was closed manually (user cancelled)
    // Delay starting the poll to give time for OAuth flow to begin
    // and for postMessage to be received after callback
    setTimeout(() => {
      if (hasReceivedResponse || isCleanedUp) return;
      
      popupCheckInterval = setInterval(() => {
        // Skip check if we've received a response
        if (hasReceivedResponse) {
          if (popupCheckInterval) {
            clearInterval(popupCheckInterval);
            popupCheckInterval = null;
          }
          return;
        }
        
        if (popup.closed) {
          // Give a brief moment for any pending postMessage to arrive
          setTimeout(() => {
            if (!hasReceivedResponse && !isCleanedUp) {
              cleanup();
              reject({
                success: false,
                errorCode: 'USER_CANCELLED',
                message: 'Authorization was cancelled',
              });
            }
          }, 500); // Wait 500ms after popup closes to check for message
          
          // Stop polling
          if (popupCheckInterval) {
            clearInterval(popupCheckInterval);
            popupCheckInterval = null;
          }
        }
      }, 5000); // Check every 1 second instead of 500ms
    }, 10000); // Wait 2 seconds before starting to poll

    // Timeout after 5 minutes (OAuth shouldn't take longer)
    timeoutId = setTimeout(() => {
      if (!hasReceivedResponse && !isCleanedUp) {
        cleanup();
        if (popup && !popup.closed) {
          popup.close();
        }
        reject({
          success: false,
          errorCode: 'TIMEOUT',
          message: 'Authorization timed out',
        });
      }
    }, 5 * 60 * 1000);
  });
};

/**
 * Check if an error is a popup blocked error
 * @param {Error|Object} error - The error to check
 * @returns {boolean}
 */
export const isPopupBlockedError = (error) => {
  return error?.message === 'POPUP_BLOCKED' || error?.errorCode === 'POPUP_BLOCKED';
};

/**
 * Check if an error is a user cancelled error
 * @param {Error|Object} error - The error to check
 * @returns {boolean}
 */
export const isUserCancelledError = (error) => {
  return error?.errorCode === 'USER_CANCELLED';
};

/**
 * Get user-friendly error message
 * @param {Error|Object} error - The error object
 * @returns {string} User-friendly error message
 */
export const getOAuthErrorMessage = (error) => {
  if (isPopupBlockedError(error)) {
    return 'Popup was blocked. Please allow popups for this site and try again.';
  }
  
  if (isUserCancelledError(error)) {
    return 'Authorization was cancelled.';
  }

  if (error?.errorCode === 'TIMEOUT') {
    return 'Authorization timed out. Please try again.';
  }

  return error?.message || 'An error occurred during authorization.';
};